import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import { osirisTokens } from '@/theme/tokens';

export type StateBadgeVariant =
  | 'ready'
  | 'takeoff'
  | 'land'
  | 'stabilize'
  | 'offline'
  | 'armed'
  | 'manual'
  | 'mission'
  | 'hold'
  | 'live'
  | 'rtl'
  | 'guided'
  | 'critical'
  | 'neutral';

interface StateBadgeProps {
  variant?: StateBadgeVariant;
  /** 'small' = 16px chip-complex (Figma 29185:108541) used in the drone-list tabs */
  size?: 'medium' | 'small';
  children: ReactNode;
  className?: string;
}

// Color Coding spec (Figma 29324:80050): green = ready to fly, blue = any
// command in flight, orange = manual flight, red = critical, grey = swarm
const VARIANT_STYLES: Record<StateBadgeVariant, { bg: string; color: string }> = {
  ready: { bg: osirisTokens.badge.ready, color: '#ffffff' },
  stabilize: { bg: osirisTokens.badge.ready, color: '#ffffff' },
  takeoff: { bg: osirisTokens.badge.takeoff, color: '#ffffff' },
  land: { bg: osirisTokens.badge.inFlight, color: '#ffffff' },
  mission: { bg: osirisTokens.badge.inFlight, color: '#ffffff' },
  hold: { bg: osirisTokens.badge.inFlight, color: '#ffffff' },
  rtl: { bg: osirisTokens.badge.inFlight, color: '#ffffff' },
  guided: { bg: osirisTokens.badge.inFlight, color: '#ffffff' },
  armed: { bg: osirisTokens.badge.manual, color: '#ffffff' },
  manual: { bg: osirisTokens.badge.manual, color: '#ffffff' },
  live: { bg: osirisTokens.badge.critical, color: '#ffffff' },
  critical: { bg: osirisTokens.badge.critical, color: '#ffffff' },
  offline: { bg: osirisTokens.state.offline, color: '#ffffff' },
  neutral: { bg: osirisTokens.alpha.dark200, color: osirisTokens.text.primary },
};

export function StateBadge({
  variant = 'neutral',
  size = 'medium',
  children,
  className,
}: StateBadgeProps) {
  const style = VARIANT_STYLES[variant];
  const small = size === 'small';
  return (
    <Box
      component="span"
      className={`state-badge state-badge-${variant}${small ? ' state-badge-small' : ''}${className ? ' ' + className : ''}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: small ? 16 : 20,
        px: small ? '4px' : 0.75,
        minWidth: small ? 24 : undefined,
        gap: small ? 0.5 : undefined,
        bgcolor: style.bg,
        color: small ? osirisTokens.text.secondary : style.color,
        fontFamily: osirisTokens.fonts.mono,
        fontWeight: small ? 500 : 600,
        fontSize: 11,
        letterSpacing: small ? '0.02em' : '0.06em',
        textTransform: 'uppercase',
        borderRadius: small ? '1px' : 0.25,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  );
}
